import React, { useState, useRef, useEffect } from 'react';
import '../../styles/components/NavigationTabs.css';

export interface TabItem {
  id: string;
  label: string;
  icon: string;
  searchable?: boolean;
  status?: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';
}

export interface NavigationTabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  searchValue?: string;
  onSearch?: (text: string) => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  searchStats?: {
    currentMatch: number;
    totalMatches: number;
  };
}

const NavigationTabs: React.FC<NavigationTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  searchValue,
  onSearch,
  onNavigatePrevious,
  onNavigateNext,
  searchStats
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  
  const activeTabObj = tabs.find(tab => tab.id === activeTab);
  const isSearchable = activeTabObj?.searchable || false;

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 10);
    }
  }, [isSearchExpanded]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchContainerRef.current && 
        !searchContainerRef.current.contains(event.target as Node) && 
        isSearchExpanded && 
        !searchValue
      ) {
        setIsSearchExpanded(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded, searchValue]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch && onSearch(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchStats && searchStats.totalMatches > 0) {
      e.preventDefault();
      
      if (onNavigateNext) {
        onNavigateNext();
      }
    }
  };

  const clearSearch = () => {
    onSearch && onSearch('');
  };
  
  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  return (
    <div className="navigation-tabs">
      <div className="tabs-left">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => onTabChange(tab.id)}
            style={{ gap: '4px' }}
          >
            <span className="material-symbols-outlined">{tab.icon}</span>
            <span className="truncate-tab-label" title={tab.label}>{tab.label}</span>
            {tab.status && (
              <span
                className={`material-symbols-outlined tab-analysis-status status-${tab.status} ${tab.status === 'running' ? 'spin' : ''}`}
                title={`${tab.label} — ${tab.status}`}
              >
                {tab.status === 'succeeded'
                  ? 'check_circle'
                  : tab.status === 'failed'
                    ? 'error'
                    : tab.status === 'skipped'
                      ? 'remove_circle'
                      : tab.status === 'running'
                        ? 'progress_activity'
                        : 'radio_button_unchecked'}
              </span>
            )}
          </button>
        ))}
      </div>

      {isSearchable && (
        <div className="search" ref={searchContainerRef}>
          {searchStats && searchStats.totalMatches > 0 && (
            <div className="search-navigation">
              <span className="search-results-count">
                {searchStats.currentMatch} of {searchStats.totalMatches}
              </span>
              <button
                className="nav-button"
                onClick={onNavigatePrevious}
                disabled={!onNavigatePrevious || searchStats.totalMatches === 0}
              >
                <span className="material-symbols-outlined">arrow_upward</span>
              </button>
              <button
                className="nav-button"
                onClick={onNavigateNext}
                disabled={!onNavigateNext || searchStats.totalMatches === 0}
              >
                <span className="material-symbols-outlined">arrow_downward</span>
              </button>
            </div>
          )}
          <span 
            className="material-symbols-outlined search-icon" 
            onClick={toggleSearch}
          >
            search
          </span>

          <div className={`search-input-container ${isSearchExpanded ? 'expanded' : 'collapsed'}`}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={`Search within ${activeTabObj?.label?.toLowerCase() || 'content'}`}
              value={searchValue || ''}
              onChange={handleSearchInput}
              onKeyDown={handleSearchKeyDown}
              className="search-input"
            />
            
            {searchValue && (
              <button className="clear-search" onClick={clearSearch}>
                <span className="material-symbols-outlined">close</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationTabs; 